import React, { useEffect, useState, useRef } from 'react';
import { debounce } from 'throttle-debounce';
import Input from '@welldev-group/rcl-input';
import Icon from '@welldev-group/rcl-icon';
import Card from '@welldev-group/rcl-card';
import Button from '@welldev-group/rcl-button';
import { I18nText, SortingPill, Link } from '@welldev-group/react-comp-lib';
import { Header } from '@welldev-group/rcl-typography';
import {
  NumberParam,
  StringParam,
  useQueryParams,
  withDefault,
  ArrayParam,
} from 'use-query-params';
import routes from '../../../../shared/routes';
import withQueryParamsProvider from '../../../../shared/components/withQueryParamsProvider';
import httpClient from '../../../../shared/libraries/httpClient';
import PaginationWithTranslation from '../../../../shared/components/Pagination/PaginationWithTranslation';
import AffiliateTable from './AffiliateTable';
import DropDownFilter from '../../../../shared/components/Filters/DropDownFilter';

const AffiliateList = () => {
  const [affiliates, setAffiliates] = useState([]);
  const [totalPages, setTotalPage] = useState(null);
  const [selectedWorkspaces, setSelectedWorkspaces] = useState([]);
  const [deactivatedScope, setDeactivatedScope] = useState(false);
  const [selectedPlans, setSelectedPlans] = useState([]);
  const [selectedAccountManagers, setSelectedAccountManagers] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [query, setQuery] = useQueryParams({
    q: withDefault(StringParam, ''),
    page: NumberParam,
    sortColumn: StringParam,
    sortDirection: StringParam,
    planIds: withDefault(ArrayParam, []),
    workspaceIds: withDefault(ArrayParam, []),
    accountManagerIds: withDefault(ArrayParam, []),
  });

  const {
    q,
    page,
    sortColumn,
    sortDirection,
    planIds,
    workspaceIds,
    accountManagerIds,
  } = query;

  const [localSearchQuery, setLocalSearchQuery] = useState(q);

  const setInitialParams = data => {
    // We only set the params onces when there is nothing in the url
    if (!query.page) {
      setQuery(
        {
          q: '',
          page: data.pagination.currentPage,
          sortColumn: data.sort.column,
          sortDirection: data.sort.direction,
        },
        'replaceIn',
        // use replaceIn as we don't want another navigation to be added to the browser history
      );
    }
  };

  const hasFilterParams = [planIds, workspaceIds, accountManagerIds].some(
    value => value.length > 0,
  );

  const fetchAffiliates = async (
    searchQuery,
    newPage,
    column,
    direction,
    newPlanIds,
    deactivated,
    newWorkspaceIds,
    newAccountManagerIds,
  ) => {
    setIsLoading(true);
    const { data } = await httpClient.get(
      routes.affiliates.list({
        deactivated,
        q: searchQuery,
        page: newPage,
        column,
        direction,
        'plan_ids[]': newPlanIds,
        'workspace_ids[]': newWorkspaceIds,
        'account_manager_ids[]': newAccountManagerIds,
      }),
    );

    setIsLoading(false);
    setAffiliates(data.affiliates);
    setInitialParams(data);
    setTotalPage(data.pagination.totalPages);
  };

  const setSearchQueryInParams = useRef(
    debounce(500, value => {
      setQuery({ q: value, page: 1 });
    }),
  ).current;

  const onSearchParamsChange = value => {
    // We keep a local state here as we don't want to do an AJAX call for each key the user presses
    // This allows us to keep the UI responsive and fetch new data with a delay
    setLocalSearchQuery(value);
    setSearchQueryInParams(value);
  };

  const toggleDirection = () => {
    if (sortDirection === 'up') {
      setQuery({ sortDirection: 'down' });
    } else {
      setQuery({ sortDirection: 'up' });
    }
  };

  const setSortOrder = chosenColumn => {
    if (sortColumn === chosenColumn) {
      toggleDirection();
    } else {
      setQuery({ sortColumn: chosenColumn, sortDirection: 'up' });
    }
  };

  const restructureAccounManagersOptions = (
    accountManagersOptions,
    callback,
  ) => {
    callback(
      accountManagersOptions.map(({ id, email, name }) => ({
        value: id,
        label: (
          <div key={id} className="affiliate-list__modifier--filter-label">
            <div className="affiliate-list__modifier--filter-label-first-element">
              {name}
            </div>
            <div className="affiliate-list__modifier--filter-label-last-element">
              {email}
            </div>
          </div>
        ),
      })),
    );
  };

  const restructureWorkspacesOptions = (workspacesOptions, callback) => {
    callback(
      workspacesOptions.map(workspace => ({
        value: workspace.id,
        label: (
          <div className="affiliate-list__modifier--filter-label">
            <div className="affiliate-list__modifier--filter-label-first-element">
              {workspace.name}
            </div>
            <div className="affiliate-list__modifier--filter-label-last-element">
              {workspace.identifier}
            </div>
          </div>
        ),
      })),
    );
  };

  const clearFilters = () => {
    setQuery(
      {
        planIds: [],
        workspaceIds: [],
        accountManagerIds: [],
      },
      'replaceIn',
    );
    setSelectedPlans([]);
    setSelectedWorkspaces([]);
    setSelectedAccountManagers([]);
  };

  const resetQueryParams = () => {
    setQuery({
      q: '',
      page: 1,
      sortColumn: 'client_name',
      sortDirection: 'up',
    });
    setLocalSearchQuery('');
    clearFilters();
  };

  const getValues = optionsArray => optionsArray.map(option => option.value);
  const fetchAffiliatesWithParams = () => {
    fetchAffiliates(
      q,
      page,
      sortColumn,
      sortDirection,
      planIds,
      deactivatedScope,
      workspaceIds,
      accountManagerIds,
    );
  };

  const onConfirmationModalDismiss = () => {
    resetQueryParams();
    fetchAffiliatesWithParams();
  };
  useEffect(() => {
    fetchAffiliatesWithParams();
  }, [
    q,
    page,
    sortColumn,
    sortDirection,
    planIds,
    workspaceIds,
    accountManagerIds,
  ]);

  useEffect(() => {
    resetQueryParams();
    fetchAffiliatesWithParams();
  }, [deactivatedScope]);

  const handleAccountManagerChange = newSelectedAccountManagers => {
    setSelectedAccountManagers(newSelectedAccountManagers);
    setQuery({
      accountManagerIds: getValues(newSelectedAccountManagers),
      page: 1,
    });
  };

  return (
    <div className="affiliate-list">
      <div className="affiliate-list__header">
        <Header level={4} weight="medium">
          <I18nText id="affiliate_three_sixty.header" />
        </Header>
      </div>
      <div className="grid">
        <div className="col-lg-4 col-md-4 col-bleed col-xs-12 affiliate-list__search">
          <Input
            value={localSearchQuery}
            onChange={onSearchParamsChange}
            preIcon={<Icon name="search" />}
            placeholder={I18n.t(
              'components.affiliate_three_sixty.affiliate_list.placeholder.search',
            )}
          />
        </div>
      </div>
      <div className="affiliate-list__modifier">
        <div className="affiliate-list__modifier--sorting">
          <div className="affiliate-list__modifier--sorting-with-right-padding">
            <SortingPill
              size="small"
              sortDirection={
                sortColumn === 'client_name' ? sortDirection : null
              }
              parameterName={I18n.t(
                'components.affiliate_three_sixty.affiliate_list.attributes.name',
              )}
              onClick={() => setSortOrder('client_name')}
            />
          </div>
          <div className="affiliate-list__modifier--sorting-with-right-padding">
            <SortingPill
              size="small"
              sortDirection={
                sortColumn === 'active_users' ? sortDirection : null
              }
              parameterName={I18n.t(
                'components.affiliate_three_sixty.affiliate_list.attributes.active_users',
              )}
              onClick={() => setSortOrder('active_users')}
            />
          </div>
          <div className="affiliate-list__modifier--sorting-with-right-padding">
            <SortingPill
              size="small"
              sortDirection={sortColumn === 'workspaces' ? sortDirection : null}
              parameterName={I18n.t(
                'components.affiliate_three_sixty.affiliate_list.attributes.workspaces',
              )}
              onClick={() => setSortOrder('workspaces')}
            />
          </div>
          <div className="affiliate-list__modifier--sorting-with-right-padding">
            <SortingPill
              size="small"
              sortDirection={sortColumn === 'last_login' ? sortDirection : null}
              parameterName={I18n.t(
                'components.affiliate_three_sixty.affiliate_list.attributes.last_login',
              )}
              onClick={() => setSortOrder('last_login')}
            />
          </div>
        </div>
        <div className="affiliate-list__modifier--filter">
          <div className="affiliate-list__modifier--filter-link">
            {/* eslint-disable jsx-a11y/anchor-is-valid */}
            {deactivatedScope ? (
              <Link
                onClick={() => setDeactivatedScope(false)}
                size="small"
                type="button"
                modifier="default"
              >
                <I18nText id="affiliate_three_sixty.affiliate.button_label.active" />
              </Link>
            ) : (
              <Link
                onClick={() => setDeactivatedScope(true)}
                size="small"
                type="button"
                modifier="default"
              >
                <I18nText id="affiliate_three_sixty.affiliate.button_label.deactivated" />
              </Link>
            )}
            {/* eslint-enable jsx-a11y/anchor-is-valid */}
          </div>
          <div className="affiliate-list__modifier--filter-with-right-padding">
            <DropDownFilter
              type="plan"
              label={
                <I18nText id="affiliate_three_sixty.affiliate_list.attributes.plan" />
              }
              onChange={newSelectedPlans => {
                setSelectedPlans(newSelectedPlans);
                setQuery({ planIds: getValues(newSelectedPlans), page: 1 });
              }}
              selectedOptions={selectedPlans}
              isSearchable={false}
              preIcon={<Icon name="plan" size="medium" color="default" />}
            />
          </div>
          <div className="affiliate-list__modifier--filter-with-right-padding">
            <DropDownFilter
              type="workspaces"
              label={
                <I18nText id="affiliate_three_sixty.affiliate_list.attributes.workspaces" />
              }
              onChange={newSelectedWorkspaces => {
                setSelectedWorkspaces(newSelectedWorkspaces);
                setQuery({
                  workspaceIds: getValues(newSelectedWorkspaces),
                  page: 1,
                });
              }}
              selectedOptions={selectedWorkspaces}
              placeholderText={I18n.t(
                'components.affiliate_three_sixty.affiliate_list.placeholder.filter.workspaces',
              )}
              onRestructuringDropDownOptions={restructureWorkspacesOptions}
              filterParams={{ active: true }}
              preIcon={<Icon name="widgets" color="default" />}
            />
          </div>

          <div className="affiliate-list__modifier--filter-with-right-padding">
            <DropDownFilter
              type="accountManagers"
              label={I18n.t(
                'components.affiliate_three_sixty.affiliate_list.attributes.account_managers',
              )}
              onChange={handleAccountManagerChange}
              selectedOptions={selectedAccountManagers}
              placeholderText={I18n.t(
                'components.affiliate_three_sixty.affiliate_list.placeholder.filter.account_managers',
              )}
              onRestructuringDropDownOptions={restructureAccounManagersOptions}
              filterParams={{ active: true }}
              preIcon={<Icon name="widgets" color="default" />}
            />
          </div>

          {hasFilterParams && (
            <div className="affiliate-list__modifier--filter-reset">
              <Button
                version="v2"
                size="small"
                label={
                  <div className="affiliate-list__modifier--filter-reset-button">
                    <div className="affiliate-list__modifier--filter-reset-button-icon">
                      <Icon name="close" size="small" />
                    </div>
                    <I18nText id="affiliate_three_sixty.affiliate.button_label.clear_filters" />
                  </div>
                }
                onClick={clearFilters}
              />
            </div>
          )}
        </div>
      </div>

      <AffiliateTable
        affiliates={affiliates}
        deactivatedAffiliates={deactivatedScope}
        isLoading={isLoading}
        onConfirmationModalDismiss={onConfirmationModalDismiss}
      />
      {!isLoading && !(affiliates.length > 0) && (
        <Card
          version="v2"
          size="full"
          color="tertiary"
          className="affiliate-list__empty"
          emptyCardImageSrc={
            <Icon name="af360" color="tertiary" showBGColor={true} />
          }
          emptyCardText={
            <I18nText id="affiliate_three_sixty.card.empty.text" />
          }
        />
      )}
      {!isLoading && (
        <div className="affiliate-list__pagination">
          <PaginationWithTranslation
            currentPage={page}
            totalPages={totalPages}
            onPaginationClick={value => setQuery({ page: value })}
          />
        </div>
      )}
    </div>
  );
};

export default withQueryParamsProvider(AffiliateList);
